import { DatePipe, DecimalPipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute } from "@angular/router";
import { PrintLabelsDialogComponent } from "@components/print-labels-dialog/print-labels-dialog.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { environment } from "@environments/environment";
import { AppStateService, RequestService } from "@services/index";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "../../../components/filters/form-autocomplete/form-autocomplete.component";

@Component({
  selector: "fh-order-details",
  templateUrl: "./order-details.component.html",
  styleUrls: ["./order-details.component.css"],
  standalone: true,
  imports: [
    DecimalPipe,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    FormAutocompleteComponent,
    RefreshButtonComponent,
  ],
})
export class OrderDetailsComponent implements OnInit {
  list: any[] = [];
  app_url = environment.backendURL;
  orderId: string;
  orderDetails: any = {};

  /** Merchant Remarks for Customer: shown on the customer's My Orders. */
  merchantRemarks = "";
  merchantRemarksSaved = true;
  savingMerchantRemarks = false;

  deliveryStatusOptions: FormAutocompleteOption<string>[] = [
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
    { value: "refunded", label: "Refunded" },
  ];

  constructor(
    private requestService: RequestService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private appStateService: AppStateService
  ) {
    this.orderId = this.route.snapshot.paramMap.get("id") as string;
  }

  /** Labels are made for orders in a live market (as on the order list). */
  get canPrintLabel(): boolean {
    return this.appStateService
      .liveMarketCodes()
      .includes(String(this.orderDetails?.iso2 ?? "").toUpperCase());
  }

  /** This order's shipping label (the merchant's own parcel; orders in a live market). */
  printLabel(): void {
    this.dialog.open(PrintLabelsDialogComponent, {
      data: { orderIds: [this.orderId] },
      width: "420px",
    });
  }

  ngOnInit(): void {
    this.fetchOrder();
  }

  // Bound wrapper so fh-refresh-button gets a stable, correctly-`this`-bound reference.
  refreshOrder = () => this.fetchOrder();

  fetchOrder() {
    this.requestService.get(`/api/v2/order-details/${this.orderId}`).subscribe((res: any) => {
      if (res.status === true && res.data?.orderDetails) {
        const { details: itemList, ...rest } = res.data.orderDetails;
        this.list = itemList || [];
        this.orderDetails = rest;
        this.merchantRemarks = rest.merchant_remarks ?? "";
        this.merchantRemarksSaved = true;
      }
    });
  }

  fetchDeliveryRemarks = async () => {
    this.requestService.get(`/api/v1/delivery-remarks?orderId=${this.orderId}`);
  };

  updateItemDeliveryStatus(item: any) {
    this.requestService
      .post(`/api/v1/update_item_delivery_status`, {
        orderDetailId: item.id,
        deliveryStatus: item.delivery_status,
      })
      .subscribe((res: any) => {
        if (res.status === true) {
          console.log("Item delivery status updated");
        }
      });
  }

  saveMerchantRemarks() {
    this.savingMerchantRemarks = true;
    const sent = this.merchantRemarks.slice(0, 500);
    this.requestService
      .post(`/api/v1/order-merchant-remarks`, {
        orderId: this.orderId,
        remarks: sent,
      })
      .subscribe({
        next: (res: any) => {
          this.savingMerchantRemarks = false;
          // Only "Saved" if nothing was typed while the save was in flight.
          if (res.status === true) this.merchantRemarksSaved = this.merchantRemarks === sent;
          else alert(res.message || "The remarks could not be saved");
        },
        error: () => {
          this.savingMerchantRemarks = false;
          alert("The remarks could not be saved");
        },
      });
  }

  updateItemRemarks(item: any) {
    this.requestService
      .post(`/api/v1/update_item_remarks`, {
        orderDetailId: item.id,
        remarks: item.remarks,
      })
      .subscribe((res: any) => {
        if (res.status === true) {
          console.log("Item remarks updated");
        }
      });
  }
}
